/**
 * @fileoverview Local-first service for the admin-shaped legacy schema manifest.
 * @module system-web/legacy-schema-manifest-service
 *
 * Responsibilities:
 * - Read the persisted schema manifest from Dexie (liveQuery-safe).
 * - POST schema manifest saves through system-api.
 * - Provide a helper that asks system-api to build/refresh the manifest from legacy SQL.
 *
 * Dependencies:
 * - `@firesquirrel/local-db` for system Dexie access.
 * - `../apiClient` `apiFetch` for API calls.
 *
 * Architecture:
 * - The schema manifest is local-first: system Mongo is the server-of-record;
 *   Dexie `legacy_schema_manifest_tables` mirrors it via system sync pull.
 * - The client POSTs edits (disable toggles, added FKs) to system-api and then
 *   reads back via Dexie liveQuery after the next sync pull.
 * - The "populate from SQL" step is a server-side online action: the frontend only
 *   requests population; system-api queries legacy SQL, persists Mongo, then system
 *   sync pulls the authoritative result into Dexie.
 *
 * Notes:
 * - `getSchemaManifest` is liveQuery-safe (pure Dexie read).
 * - `populateFromSql`, `saveSchemaManifest` are imperative mutations; never call
 *   them inside `liveQuery`.
 */

import { getSystemDb } from '@firesquirrel/local-db';
import { apiFetch } from '../apiClient';

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

/**
 * @contract SchemaManifestTableEntry
 *
 * Purpose:
 * - One table row in the persisted schema manifest, with admin-controlled flags.
 *
 * Fields:
 * - schema: SQL schema name (typically 'dbo').
 * - table: SQL table name.
 * - primaryKey: The explicit primary key column for this table.
 * - disabled: Admin has toggled this table off — it is hidden from dataset builders.
 * - disabledColumns: Columns the admin has explicitly excluded from migration use.
 * - notes: Optional admin comment on this table.
 *
 * Rules:
 * - 'disabled' only affects dataset builder visibility; does not touch INFORMATION_SCHEMA.
 * - primaryKey must match one of the returned columns.
 *
 * Sync:
 * - Embedded in SchemaManifestDoc.tables[]; mirrored to Dexie via system sync.
 */
export interface SchemaManifestTableEntry {
  schema: string;
  table: string;
  primaryKey: string;
  columns?: SchemaManifestColumn[];
  disabled: boolean;
  disabledColumns?: string[];
  notes?: string;
}

export interface SchemaManifestColumn {
  column: string;
  dataType: string;
  isNullable: boolean;
  maxLength?: number;
  ordinalPosition: number;
  isPrimaryKey: boolean;
}

/**
 * @contract SchemaManifestFk
 *
 * Purpose:
 * - An admin-declared FK relationship between two tables in the legacy schema.
 * - Used because Streamline rarely declares FK constraints at the DDL level.
 *
 * Fields:
 * - parentTable: The parent (referenced) table name.
 * - parentColumn: The column on the parent table that is referenced.
 * - childTable: The child (referencing) table name.
 * - childColumn: The column on the child table that holds the FK value.
 * - label: Optional human-readable description of the relationship.
 *
 * Rules:
 * - Both parentTable and childTable must exist in the manifest table list.
 * - Server validates this before persisting.
 *
 * Sync:
 * - Embedded in SchemaManifestDoc.declaredFks[]; mirrored to Dexie via system sync.
 */
export interface SchemaManifestFk {
  constraintName?: string;
  parentSchema?: string;
  parentTable: string;
  parentColumn: string;
  childSchema?: string;
  childTable: string;
  childColumn: string;
  label?: string;
}

/**
 * @contract SchemaManifestDoc
 *
 * Purpose:
 * - The full persisted schema manifest document — one per legacy database.
 * - Server-authoritative; Dexie mirrors via system sync pull.
 *
 * Fields:
 * - _id: MongoDB ObjectId as string.
 * - legacyDb: The legacy database this manifest describes.
 * - tables: All discovered tables from INFORMATION_SCHEMA with admin disable flags.
 * - declaredFks: Admin-declared FK relationships between tables.
 * - updatedBy: System-admin user who last saved.
 * - updatedAt: When the manifest was last saved.
 * - _deleted: Soft-delete tombstone.
 *
 * Rules:
 * - Only one non-deleted document per legacyDb.
 *
 * Sync:
 * - Source of truth: system Mongo `legacySchemaManifestTables`.
 * - Dexie table: `legacy_schema_manifest_tables`.
 */
export interface SchemaManifestDoc {
  _id: string;
  legacyDb: string;
  tables: SchemaManifestTableEntry[];
  declaredFks: SchemaManifestFk[];
  updatedBy: string;
  updatedAt: string;
  _deleted?: boolean;
}

export interface SchemaManifestTableDoc extends SchemaManifestTableEntry {
  _id: string;
  legacyDb: string;
  foreignKeys?: Array<SchemaManifestFk & { direction?: 'parent' | 'child' }>;
  updatedBy?: string;
  updatedAt?: string;
  _deleted?: boolean;
}

// ---------------------------------------------------------------------------
// Dexie read (liveQuery-safe)
// ---------------------------------------------------------------------------

/**
 * @localFirst
 * Source of Truth: system Dexie `legacy_schema_manifest_tables` (mirrored from Mongo).
 * Write Flow:      saveSchemaManifest() / populateFromSql() POST to server; sync pulls result.
 * Sync Strategy:   System sync pull mirrors legacySchemaManifestTables.
 * Conflict Resolution: Server wins.
 *
 * Return value is suitable for use inside `liveQuery(() => getSchemaManifest(...))`.
 * NEVER write to Dexie from inside liveQuery.
 *
 * @errorHandling
 * Strategy:   Returns null when no manifest exists. Throws only on Dexie open failure.
 * User Impact: null = "no manifest yet" → UI triggers populateFromSql automatically.
 * Logging:     Not required for a pure read.
 */
export async function getSchemaManifest(legacyDb: string): Promise<SchemaManifestDoc | null> {
  const db = await getSystemDb();
  const tableRows = await db.legacy_schema_manifest_tables.toArray();
  const tableDocs = (tableRows as SchemaManifestTableDoc[])
    .filter((row) => row.legacyDb === legacyDb && !row._deleted);
  const fkMap = new Map<string, SchemaManifestFk>();
  for (const table of tableDocs) {
    for (const fk of table.foreignKeys ?? []) {
      const key = `${fk.parentSchema ?? ''}.${fk.parentTable}.${fk.parentColumn}->${fk.childSchema ?? ''}.${fk.childTable}.${fk.childColumn}`.toLowerCase();
      fkMap.set(key, fk);
    }
  }
  if (tableDocs.length === 0) return null;
  return {
    _id: `local-${legacyDb}`,
    legacyDb,
    tables: tableDocs.map(({ foreignKeys: _foreignKeys, ...table }) => ({
      ...table,
      disabledColumns: table.disabledColumns ?? [],
    })),
    declaredFks: Array.from(fkMap.values()),
    updatedBy: '',
    updatedAt: tableDocs.reduce((latest, row) => row.updatedAt && row.updatedAt > latest ? row.updatedAt : latest, ''),
    _deleted: false,
  };
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/**
 * @localFirst
 * Source of Truth: system Mongo (server-authoritative).
 * Write Flow:
 *   1. POST to /api/system/legacy-migration/schema-manifest/:legacyDb.
 *   2. Server validates FKs reference known tables.
 *   3. Returns the saved manifest doc.
 *   4. Dexie will receive the authoritative doc via the next system sync pull.
 * Sync Strategy:   Pull-based after server write.
 * Conflict Resolution: Server wins; 422 returned on validation failure.
 *
 * @errorHandling
 * Strategy:   Throws on non-200 responses; 422 includes FK validation issues.
 * User Impact: Schema Manifest editor shows error banner inline.
 * Logging:    REQUIRED — log failures with legacyDb.
 */
export async function saveSchemaManifest(
  legacyDb: string,
  tables: SchemaManifestTableEntry[],
  declaredFks: SchemaManifestFk[],
): Promise<SchemaManifestDoc> {
  const result = await apiFetch(
    `/api/system/legacy-migration/schema-manifest/${encodeURIComponent(legacyDb)}`,
    { method: 'POST', body: JSON.stringify({ tables, declaredFks }) },
  );
  return result.manifest as SchemaManifestDoc;
}

/**
 * Request server-side legacy SQL discovery and manifest persistence.
 *
 * @process populateFromSql
 * Steps:
 *   1. POST to /schema-manifest/:legacyDb/populate.
 *   2. system-api fetches tables/FKs from legacy SQL and preserves existing admin edits.
 *   3. system-api persists the authoritative manifest to Mongo.
 *   4. System sync pulls the saved manifest into Dexie; UI renders from liveQuery.
 *
 * Inputs:
 *   - legacyDb: The legacy database to populate from.
 *   - existing: Current manifest doc; retained for call-site compatibility but not used client-side.
 *
 * Outputs:
 *   - The saved SchemaManifestDoc returned by the server for diagnostics only.
 *
 * Failure Handling:
 *   - Throws if the server-side discovery/save fails.
 *
 * Idempotency:
 *   - REQUIRED — re-running populate preserves all admin flags and FKs.
 *
 * @errorHandling
 * Strategy:   Throws on API failure; caller surfaces progress UI.
 * User Impact: Progress state shown in the Schema Manifest editor.
 * Logging:    REQUIRED — log table count and merge delta.
 */
export async function populateFromSql(
  legacyDb: string,
  _existing: SchemaManifestDoc | null,
): Promise<SchemaManifestDoc> {
  const result = await apiFetch(
    `/api/system/legacy-migration/schema-manifest/${encodeURIComponent(legacyDb)}/populate`,
    { method: 'POST', body: JSON.stringify({}) },
  );
  return result.manifest as SchemaManifestDoc;
}
